import { useEffect } from "react";
import { useBucketListStore } from "../services/store";
import { firestoreService } from "../services/firestore";
import { useAuth } from "../contexts/AuthContext";
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { BucketItem } from "../types";

/**
 * Hook that syncs local store with Firestore
 * Automatically subscribes to real-time updates
 * Also handles migration from nested to root-level collections
 */
export const useFirebaseSync = () => {
  const { user } = useAuth();
  const { setItems } = useBucketListStore();

  useEffect(() => {
    if (!user) {
      // Clear items when logged out
      setItems([]);
      return;
    }

    // Initial load from Firestore
    const loadItems = async () => {
      try {
        // First, check for old nested items and migrate them
        await migrateNestedItems(user.uid);
        
        // Then load items from root-level collection
        const firestoreItems = await firestoreService.getItems(user.uid);

        // Migrate old items: add ownerId and sharedWith if missing
        const migratedItems = firestoreItems.map((item: BucketItem) => {
          const needsMigration = !item.ownerId || !item.sharedWith;
          if (needsMigration) {
            return {
              ...item,
              ownerId: item.ownerId || user.uid,
              sharedWith: item.sharedWith || [],
            };
          }
          return item;
        });

        // Save migrated items back to Firestore
        const itemsToMigrate = migratedItems.filter((_item: BucketItem, index: number) => {
          const original = firestoreItems[index];
          return !original.ownerId || !original.sharedWith;
        });
        
        if (itemsToMigrate.length > 0) {
          await firestoreService.batchSaveItems(itemsToMigrate);
        }

        // Merge with local items (in case user added items offline)
        const localItems = useBucketListStore.getState().items;
        const mergedItems = mergeItems(localItems, migratedItems);

        setItems(mergedItems);

        // Upload any local-only items to Firestore
        const localOnlyItems = localItems.filter(
          (local: BucketItem) =>
            !migratedItems.some(
              (firebase: BucketItem) => firebase.id === local.id,
            ),
        );

        if (localOnlyItems.length > 0) {
          // Ensure local items have ownerId and sharedWith
          const migratedLocalItems = localOnlyItems.map((item: BucketItem) => ({
            ...item,
            ownerId: item.ownerId || user.uid,
            sharedWith: item.sharedWith || [],
          }));
          await firestoreService.batchSaveItems(migratedLocalItems);
        }
      } catch (error) {
        console.error('Error loading items:', error);
      }
    };

    loadItems();

    // Subscribe to real-time updates
    const unsubscribe = firestoreService.subscribeToItems(
      user.uid,
      (firestoreItems: BucketItem[]) => {
        // Ensure all items have ownerId and sharedWith (safety check)
        const validatedItems = firestoreItems.map((item: BucketItem) => ({
          ...item,
          ownerId: item.ownerId || '',
          sharedWith: item.sharedWith || [],
        }));
        setItems(validatedItems);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [user, setItems]);
};

/**
 * Migrate items from /users/{userId}/bucketItems to /bucketItems
 */
async function migrateNestedItems(userId: string) {
  try {
    const nestedItemsRef = collection(db, 'users', userId, 'bucketItems');
    const snapshot = await getDocs(nestedItemsRef);
    
    if (snapshot.empty) {
      return; // No nested items to migrate
    }
    
    const items = snapshot.docs.map(doc => {
      const data = doc.data() as BucketItem;
      return {
        ...data,
        ownerId: data.ownerId || userId,
        sharedWith: data.sharedWith || [],
      };
    });
    
    // Save to root-level collection
    let successCount = 0;
    const docIds = snapshot.docs.map(d => d.id);
    
    for (const item of items) {
      try {
        await firestoreService.saveItem(item);
        successCount++;
      } catch (err) {
        console.error(`Failed to save item ${item.id}:`, err);
      }
    }
    
    // Delete old nested items after successful migration
    if (successCount === items.length) {
      let deleteCount = 0;
      for (const docId of docIds) {
        try {
          await deleteDoc(doc(db, 'users', userId, 'bucketItems', docId));
          deleteCount++;
        } catch (err) {
          console.error(`Failed to delete nested item ${docId}:`, err);
        }
      }
    }
  } catch (error: any) {
    console.error('Error during migration:', error);
  }
}

/**
 * Merge local and Firebase items, preferring Firebase for conflicts
 */
function mergeItems(
  localItems: BucketItem[],
  firestoreItems: BucketItem[],
): BucketItem[] {
  const merged = [...firestoreItems];

  // Add local items that don't exist in Firestore
  localItems.forEach((localItem) => {
    if (!firestoreItems.some((fbItem) => fbItem.id === localItem.id)) {
      merged.push(localItem);
    }
  });

  return merged;
}
